import is from '@sindresorhus/is';
import type { JSX, ReactElement, ReactNode } from 'react';
import { isValidElement, useState } from 'react';

import { Button, type ButtonProps } from '../../core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../core/dropdown-menu/DropdownMenu';
import type { ShortcutTooltipProps } from '../../core/shortcut-tooltip/ShortcutTooltip';

export type Language<T extends string> = {
    readonly id: T;
    readonly name: ReactNode;
};

type LanguagePickerButtonProps = Omit<ButtonProps, 'children' | 'disabled'> & {
    readonly 'data-testid'?: string;
};

type LanguagePickerProps<T extends string> = {
    readonly activeText: string | undefined;
    readonly buttonProps?: LanguagePickerButtonProps;
    readonly disabled?: boolean;
    readonly isGenerating?: boolean;
    readonly languages: readonly Language<T>[];
    readonly onOpenChange?: (open: boolean) => void;
    readonly onLanguageChange: (languageId: T) => void;
    readonly open?: boolean;
    readonly portalContainer?: HTMLElement | null;
    readonly shortcutTooltip?: Omit<ShortcutTooltipProps, 'children'>;
    readonly trigger?: ReactElement;
};

export const LanguagePicker = <T extends string>({
    activeText,
    buttonProps,
    disabled = false,
    isGenerating = false,
    languages,
    onOpenChange,
    onLanguageChange,
    open,
    portalContainer,
    shortcutTooltip,
    trigger,
}: LanguagePickerProps<T>): JSX.Element => {
    const isButtonDisabled = disabled || isGenerating;
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = open ?? internalIsOpen;
    const defaultButtonProps: LanguagePickerButtonProps = {
        'data-testid': 'language-picker-button',
        size: 'sm',
        variant: 'secondary',
    };
    const handleOpenChange = (nextOpen: boolean): void => {
        if (open === undefined) {
            setInternalIsOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
    };

    return (
        <div className="shrink-0 w-fit">
            <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
                {isValidElement(trigger) ? (
                    <DropdownMenuTrigger disabled={isButtonDisabled} shortcutTooltip={shortcutTooltip}>
                        {trigger}
                    </DropdownMenuTrigger>
                ) : (
                    <DropdownMenuTrigger disabled={isButtonDisabled} shortcutTooltip={shortcutTooltip}>
                        <Button {...defaultButtonProps} {...buttonProps} disabled={isButtonDisabled} isActive={isOpen}>
                            {is.string(activeText) ? activeText : <span className="h-xxl w-[1.875rem]" />}
                        </Button>
                    </DropdownMenuTrigger>
                )}
                <DropdownMenuContent
                    align="end"
                    className="min-w-0 w-fit"
                    portalContainer={portalContainer}
                    sideOffset={5}
                >
                    {languages.map((language) => (
                        <DropdownMenuItem key={language.id} onSelect={() => onLanguageChange(language.id)}>
                            <div className="flex items-center gap-s justify-between w-full">
                                <span className="flex-[0_1_auto] min-w-0 w-full">{language.name}</span>
                            </div>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};
